import { Component, Input, inject } from '@angular/core';
import { AuthService } from 'src/app/auth.service';
import { AufnahmePunktDocument, dateString } from '../../../data-model';
import { PERSONENANGABE_VERBORGEN, personenangabenVerbergen } from '../../personenangaben';
import { verfahrenName, verfahrenVon } from '../verfahren';
import { KaMaskeId, kaMaske } from './ka-maske';
import {
	KaAnzeigeBlock,
	KaAnzeigeOptionen,
	KaHorizontAnzeige,
	kaBlockAnzeige,
	kaHorizonte,
} from './ka-anzeige';

/**
 * EIN Reiter der KA-Aufnahme: ein Block des amtlichen Aufnahmebogens zum Lesen.
 *
 * **Je Block ein eigener Reiter, so wie die SEA ihre Reiter hat** (Nutzer-Entscheidung
 * 20.09.2026): Titeldaten, Aufnahmesituation, Horizonte, Profilkennzeichnung. Vorher
 * stand alles in einem Reiter untereinander - und weil ein Reiter in bpweb nicht von
 * selbst rollt, war die Hälfte unerreichbar. Welchen Block dieser Reiter zeigt, sagt
 * {@link maske}.
 *
 * Die KA-Reiter treten an die Stelle der SEA-Reiter (Allgemein, Boden, Profilbild):
 * Ein KA-Punkt darf nie die SEA-Maske zeigen, sonst schriebe jemand SEA-Angaben in
 * eine KA-Aufnahme. Die Vegetation wird bei beiden Verfahren angeboten.
 *
 * **Bearbeitet wird wie in der SEA**: Jede Zeile trägt `bpHl` mit dem Namen der Maske
 * zu diesem Block; ein Klick führt auf `bohrpunkte/<id>/<Maske>`. Darin steckt auch das
 * Recht zum Ändern (`canIWritePoint` samt Projekt- und Sperr-Prüfung) - es gibt kein
 * zweites Tor neben dem, das für SEA-Punkte gilt. Deshalb ist dieses Bauteil am Modul
 * angemeldet und nicht "standalone": Nur so darf es `bpHl` benutzen.
 *
 * Ein Punkt mit UNBEKANNTEM Verfahren bekommt statt der Felder einen Hinweis (siehe
 * `verfahren.ts`, Regel 3) und einen einzigen Reiter. Dann ist auch nichts klickbar:
 * Woran man nichts ändern darf, soll man nicht anfassen können.
 */
@Component({
	selector: 'bp-ka-uebersicht',
	standalone: false,
	template: `
		@if (unbekannt) {
			<div class="alert alert-warning" data-testid="ka-unbekannt">
				Dieser Punkt wurde nach einem Kartierverfahren aufgenommen, das diese Fassung der App nicht kennt.
				Bitte die App aktualisieren. Bis dahin wird der Punkt nicht angezeigt und nicht geändert.
			</div>
		} @else if (maske === 'horizonte') {
			<table class="table table-sm table-dark ka-tabelle" data-testid="ka-horizonte">
				<tbody>
					@for (h of horizonte; track h.nr) {
						<tr class="ka-horizont-kopf" [bpHl]="maskeName" [hldata]="bp">
							<td class="ka-nr">{{ h.nr }}</td>
							<th scope="row" colspan="2">{{ h.kopf }}</th>
						</tr>
						@for (z of h.zeilen; track z.id) {
							<tr [bpHl]="maskeName" [hldata]="bp">
								<td class="ka-nr">{{ z.nr }}</td>
								<th scope="row">{{ z.name }}</th>
								<td [class.ka-fehlt]="z.zustand === 'nv' || z.zustand === 'nb'">{{ z.wert }}</td>
							</tr>
						}
					} @empty {
						<tr [bpHl]="maskeName" [hldata]="bp" data-testid="ka-horizonte-leer">
							<td colspan="3" class="text-body-secondary">Noch keine Horizonte aufgenommen. Zum Anlegen hier antippen.</td>
						</tr>
					}
				</tbody>
			</table>
			<div class="ka-fusszeile">{{ fussText }}</div>
		} @else {
			<table class="table table-sm table-dark ka-tabelle" [attr.data-testid]="'ka-block-' + block.block">
				<tbody>
					@for (z of block.zeilen; track z.id) {
						<tr [bpHl]="maskeName" [hldata]="bp" [attr.data-testid]="'ka-feld-' + z.id">
							<td class="ka-nr">{{ z.nr }}</td>
							<th scope="row">{{ z.name }}</th>
							<td [class.ka-fehlt]="z.zustand === 'nv' || z.zustand === 'nb'">{{ z.wert }}</td>
						</tr>
					}
				</tbody>
			</table>
			<div class="ka-fusszeile">{{ fussText }}</div>
		}
	`,
	// Die Stile stehen in einer eigenen Datei, weil am `:host` die Rollleiste des
	// Reiters hängt (siehe dort) und ein Wächter-Test sie liest.
	styleUrls: ['./ka-uebersicht.component.css'],
})
export class KaUebersichtComponent {
	private aus = inject(AuthService);

	@Input() bp!: AufnahmePunktDocument;

	/** Welchen Block des Bogens dieser Reiter zeigt. */
	@Input() maske: KaMaskeId = 'titel';

	get unbekannt(): boolean {
		return verfahrenVon(this.bp) === 'unbekannt';
	}

	/**
	 * Der Name der Maske, die diesen Block bearbeitet - gleichzeitig der Wert für
	 * `bpHl` und das letzte Stück der Adresse. Aus einer Stelle (`ka-maske.ts`),
	 * damit Klick und Route nicht auseinanderlaufen können.
	 */
	get maskeName(): string {
		return kaMaske(this.maske).route;
	}

	/** Steht unter jedem Reiter: nach welcher Anleitung aufgenommen wurde. */
	get fussText(): string {
		return `Aufnahme nach ${verfahrenName(this.bp)}, im Aufbau des amtlichen Aufnahmeformblatts. `
			+ 'Zum Ausfüllen eine Zeile antippen.';
	}

	/**
	 * Der aufbereitete Stand. Neu gerechnet wird nur, wenn ein anderer Punkt oder ein
	 * anderer Block kommt oder die Anmeldung wechselt (Gastmodus): Die Vorlage fragt
	 * bei jedem Prüflauf, und ein Getter, der jedes Mal frische Listen liefert, ist in
	 * diesem Workspace schon einmal teuer geworden.
	 */
	private stand?: {
		bp: AufnahmePunktDocument;
		maske: KaMaskeId;
		verbergen: boolean;
		block: KaAnzeigeBlock;
		horizonte: KaHorizontAnzeige[];
	};

	private aktuell() {
		const verbergen = personenangabenVerbergen(this.aus);
		if (!this.stand || this.stand.bp !== this.bp || this.stand.maske !== this.maske
			|| this.stand.verbergen !== verbergen) {
			const optionen: KaAnzeigeOptionen = {
				personenVerbergen: verbergen,
				verborgenText: PERSONENANGABE_VERBORGEN,
				datumText: dateString,
			};
			// Die Horizonte stehen im Bogen in zwei Tabellen über denselben Zeilen; in
			// der App ist es EINE Tabelle und damit ein Reiter.
			const block = kaMaske(this.maske).bloecke[0];
			this.stand = {
				bp: this.bp,
				maske: this.maske,
				verbergen,
				block: kaBlockAnzeige(this.bp, block, optionen),
				horizonte: this.maske === 'horizonte' ? kaHorizonte(this.bp, optionen) : [],
			};
		}
		return this.stand;
	}

	get block(): KaAnzeigeBlock {
		return this.aktuell().block;
	}

	get horizonte(): KaHorizontAnzeige[] {
		return this.aktuell().horizonte;
	}
}
